import { useRef, useState } from "react";

import { FieldArray } from "formik";
import {
  FlatList,
  SafeAreaView,
  ScrollView,
  TouchableOpacity,
  View,
} from "react-native";
import { Button, Card, List, useTheme } from "react-native-paper";
import { useTailwind } from "tailwind-rn";

import PublishedSwitch from "./ProductScreen/PublishedSwitch";
import SaleEmail from "./ProductScreen/SaleEmail";
import { KeyboardAwareView } from "../../../components";
import ImageUpload from "../../../components/ImageUpload";
import MultilineInput, {
  FormikMultilineInput,
} from "../../../components/MultilineInput";
import FormField from "../components/FormField";
import FormikTextInput from "../components/FormikTextInput";
import useProductFormAPI from "../contexts/ProductFormAPIContext";
import useProductFormFormik from "../hooks/useProductFormFormik";

export default function DescriptionScreen() {
  const tailwind = useTailwind();

  return (
    <KeyboardAwareView>
      <ScrollView>
        <Card style={tailwind("m-3 rounded-xl mb-40")}>
          <Card.Content style={tailwind("gap-4")}>
            <FormField label="Nombre del producto" required>
              <FormikTextInput fieldName="name" />
            </FormField>

            <FormField label="Fotos del producto">
              <PhotoUpload />
            </FormField>

            <FormField
              label="Descripción"
              info="Info visible en el detalle del producto."
            >
              <FormikMultilineInput fieldName="description" />
            </FormField>

            <BenefitsField />
            <PublishedSwitch />
            <SaleEmail />
          </Card.Content>
        </Card>
      </ScrollView>
    </KeyboardAwareView>
  );
}

function BenefitsField() {
  const { values } = useProductFormFormik();

  return (
    <FieldArray name="benefits">
      {({ push, remove }) => (
        <Benefits
          benefits={values.benefits}
          addBenefit={push}
          removeBenefit={remove}
        />
      )}
    </FieldArray>
  );
}

type BenefitsProps = {
  benefits: string[];
  addBenefit: (benefit: string) => void;
  removeBenefit: (index: number) => void;
};
function Benefits({ benefits, addBenefit, removeBenefit }: BenefitsProps) {
  const [benefit, setBenefit] = useState("");
  const [showError, setShowError] = useState(false);

  const tailwind = useTailwind();
  const { colors } = useTheme();

  function onAddBenefit() {
    if (!benefit) return;

    addBenefit(benefit);
    setBenefit("");
  }

  return (
    <View style={tailwind("gap-2")}>
      <FormField
        label="Beneficios o lista de detalles"
        info="Lista opcional de beneficios o detalles"
        error={showError ? "Agrega texto para agregar un nuevo beneficio." : ""}
      >
        <MultilineInput
          value={benefit}
          onChangeText={setBenefit}
          onFocus={() => setShowError(false)}
          onBlur={() => {
            if (!benefit && !benefits.length) setShowError(true);
          }}
          blurOnSubmit
          onSubmitEditing={onAddBenefit}
        />
      </FormField>

      <Button
        icon="plus"
        mode="contained-tonal"
        style={tailwind("self-end")}
        disabled={!benefit}
        onPress={onAddBenefit}
      >
        Agregar beneficio
      </Button>

      <FlatList
        data={benefits}
        scrollEnabled={false}
        renderItem={({ item, index }) => (
          <List.Item
            key={index}
            title={item}
            left={(props) => <List.Icon icon="star" {...props} />}
            right={(props) => (
              <TouchableOpacity onPress={() => removeBenefit(index)}>
                <List.Icon
                  icon="trash-can-outline"
                  {...props}
                  color={colors.secondary}
                />
              </TouchableOpacity>
            )}
          />
        )}
      />
    </View>
  );
}

function PhotoUpload() {
  const { values } = useProductFormFormik();
  const { uploadImageToStorage, deleteImageFromStorage } = useProductFormAPI();

  return (
    <FieldArray name="images">
      {({ push, remove, replace }) => (
        <ImageUpload
          images={values.images}
          addImage={push}
          removeImage={remove}
          replaceImage={replace}
          uploadImageToStorage={uploadImageToStorage}
          deleteImageFromStorage={deleteImageFromStorage}
        />
      )}
    </FieldArray>
  );
}
